import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import Reveal from "@/components/base/Reveal";
import InquiryForm from "@/components/feature/InquiryForm";
import { photos, images } from "@/mocks/site";

const paths = [
  {
    no: "01",
    title: "Invite Eddie",
    line: "For events, appearances, demonstrations, speaking engagements and special experiences.",
    items: [
      "Festivals",
      "Culinary events",
      "Restaurant openings",
      "Corporate events",
      "Food experiences",
      "Community events",
    ],
    to: "/invite-eddie",
    cta: "Invite Eddie",
  },
  {
    no: "02",
    title: "Hire Eddie",
    line: "For culinary experiences, private events and professional engagements.",
    items: [
      "Private events",
      "Culinary demonstrations",
      "Speaking",
      "Hosting",
      "Brand collaborations",
      "Special appearances",
    ],
    to: "/hire-eddie",
    cta: "Hire Eddie",
  },
  {
    no: "03",
    title: "Partner with Eddie",
    line: "For brands, businesses, organizations and creative collaborations.",
    items: [
      "Brand collaborations",
      "Media & film",
      "Product development",
      "Long-term partnerships",
      "Destination campaigns",
      "Content series",
    ],
    to: "#inquiry",
    cta: "Start a Conversation",
  },
];

export default function BringEddiePage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="World 04 · The Invitation"
        title={
          <>
            BRING CHEF
            <br />
            EDDIE G TO YOU
          </>
        }
        subtitle="Invite Chef Eddie G to your next event, experience, gathering or collaboration — wherever in the world that happens to be."
        image={photos.marina}
        imageClassName="object-center"
        meta={
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <a href="#inquiry" className="btn-primary">
              Send an Inquiry
              <i className="ri-arrow-down-line text-base" />
            </a>
            <Link
              to="/submit-a-locavore"
              className="btn-ghost text-background-50/85 hover:bg-background-50 hover:text-foreground-950"
            >
              Submit a Locavore
            </Link>
          </div>
        }
      />

      <section className="bg-background-50">
        <div className="w-full px-5 md:px-10 lg:px-16 py-20 md:py-28">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <Reveal>
                <p className="eyebrow">Three Ways In</p>
              </Reveal>
              <Reveal delay={120}>
                <h2 className="mt-5 font-heading text-3xl leading-[1.05] text-foreground-950 sm:text-4xl md:text-5xl">
                  However you want to{" "}
                  <span className="italic text-primary-600">work together.</span>
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 lg:self-end">
              <Reveal delay={200}>
                <p className="font-body text-base leading-relaxed text-foreground-700">
                  Whether it's one night or a long partnership, every
                  relationship starts the same way — a good conversation.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-1 gap-px overflow-hidden rounded-sm bg-foreground-950/12 md:grid-cols-3">
            {paths.map((path, i) => (
              <Reveal key={path.no} delay={i * 110}>
                <div className="flex h-full flex-col bg-background-50 p-8 md:p-10">
                  <span className="font-heading text-2xl italic text-primary-500">
                    {path.no}
                  </span>
                  <h3 className="mt-5 font-heading text-2xl text-foreground-950 md:text-3xl">
                    {path.title}
                  </h3>
                  <p className="mt-3 font-body text-sm leading-relaxed text-foreground-600">
                    {path.line}
                  </p>

                  <ul className="mt-7 flex flex-1 flex-col gap-2.5">
                    {path.items.map((item) => (
                      <li
                        key={item}
                        className="font-body text-sm text-foreground-700"
                      >
                        {item}
                      </li>
                    ))}
                  </ul>

                  {path.to.startsWith("#") ? (
                    <a
                      href={path.to}
                      className="link-underline mt-9 inline-flex w-fit items-center gap-2 font-label text-[11px] uppercase tracking-label text-primary-700"
                    >
                      {path.cta}
                      <i className="ri-arrow-right-line text-base" />
                    </a>
                  ) : (
                    <Link
                      to={path.to}
                      className="link-underline mt-9 inline-flex w-fit items-center gap-2 font-label text-[11px] uppercase tracking-label text-primary-700"
                    >
                      {path.cta}
                      <i className="ri-arrow-right-line text-base" />
                    </Link>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="inquiry" className="scroll-mt-20 bg-background-100">
        <div className="w-full px-5 md:px-10 lg:px-16 py-20 md:py-28">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-4">
              <Reveal>
                <p className="eyebrow">The Inquiry</p>
              </Reveal>
              <Reveal delay={120}>
                <h2 className="mt-5 font-heading text-3xl leading-[1.05] text-foreground-950 md:text-4xl">
                  Tell Eddie{" "}
                  <span className="italic text-primary-600">where.</span>
                </h2>
              </Reveal>
              <Reveal delay={200}>
                <p className="mt-6 font-body text-base leading-relaxed text-foreground-700">
                  Share the details and Eddie's team will follow up. The more
                  you tell us, the better the story we can build together.
                </p>
              </Reveal>
              <Reveal delay={280}>
                <div className="mt-8 aspect-[4/3] w-full overflow-hidden bg-foreground-900 image-zoom">
                  <img
                    src={images.travelRoad}
                    alt="A road leading to the horizon"
                    className="h-full w-full object-cover object-center"
                  />
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              <Reveal delay={120}>
                <InquiryForm
                  title="Send an inquiry"
                  submitLabel="Send Inquiry"
                />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="w-full px-5 md:px-10 lg:px-16 py-16 md:py-20">
          <div className="flex flex-col items-start justify-between gap-8 rounded-sm border border-foreground-950/12 p-8 md:flex-row md:items-center md:p-12">
            <div>
              <Reveal>
                <p className="eyebrow">Another Way to Help</p>
              </Reveal>
              <Reveal delay={120}>
                <h3 className="mt-4 font-heading text-2xl leading-tight text-foreground-950 md:text-3xl">
                  Know a place Eddie{" "}
                  <span className="italic text-primary-600">should visit?</span>
                </h3>
              </Reveal>
            </div>
            <Reveal delay={180}>
              <Link to="/submit-a-locavore" className="btn-primary whitespace-nowrap">
                Submit a Locavore
                <i className="ri-arrow-right-line text-base" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}